import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Navbar } from "@/components/Navbar";
import { AccountTypeSwitch } from "@/components/AccountTypeSwitch";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";
import api, { apiError } from "@/lib/api";

export default function Register() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { setUser, googleLogin } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const pending = JSON.parse(sessionStorage.getItem("pending_follow") || "null");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/register", { name, email, password });
      setUser(data);
      navigate("/");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background blueprint-grid">
      <Navbar />
      <div className="max-w-md mx-auto px-4 pt-16 sm:pt-24 pb-16">
        <div className="border border-border rounded-xl bg-card p-8 fade-up">
          <h1 className="font-heading font-extrabold text-2xl tracking-tight">{t("register")}</h1>
          <div className="mt-6">
            <AccountTypeSwitch active="user" />
          </div>
          {pending?.name && (
            <p data-testid="register-follow-prompt" className="text-sm text-accent bg-accent/10 border border-accent/30 rounded-lg px-4 py-3 mt-4">
              {t("join_mosque_prompt").replace("{name}", pending.name)}
            </p>
          )}
          <form onSubmit={submit} className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name">{t("name")}</Label>
              <Input id="name" data-testid="register-name-input" value={name} onChange={(e) => setName(e.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">{t("email")}</Label>
              <Input id="email" type="email" data-testid="register-email-input" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t("password")}</Label>
              <Input id="password" type="password" data-testid="register-password-input" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} />
            </div>
            {error && <p data-testid="register-error" className="text-sm text-destructive">{error}</p>}
            <Button type="submit" data-testid="register-submit-btn" disabled={loading} className="w-full rounded-full active:scale-95">
              {loading ? "..." : t("register")}
            </Button>
          </form>
          <div className="flex items-center gap-3 my-6">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs text-muted-foreground uppercase">{t("or")}</span>
            <div className="h-px flex-1 bg-border" />
          </div>
          <Button variant="outline" data-testid="google-register-btn" onClick={googleLogin} className="w-full rounded-full active:scale-95">
            {t("continue_google")}
          </Button>
          <p className="text-sm text-muted-foreground text-center mt-6">
            {t("have_account")}{" "}
            <Link to="/login" data-testid="goto-login-link" className="text-accent hover:underline">{t("login")}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
